import { useCallback } from 'react';
import {
  useCorporateBondScreenerSidebarFilterContext,
} from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/useCorporateBondScreenerSidebarFilterContext';
import { useBondScreenerColumnsContext } from '@/creditflowresearch/application/BondScreener/BondScreenerColumnsContext';
import type { IBondScreenerPresetConfig } from '@/creditflowresearch/domain/vo/Bondscreener';
import { applyPresetFilters, isPresetActive, removePresetFilters } from './presets/presetFilters';
import { getPresetSectionIds } from './presets/presetSectionMapping';

const PRESET_TOOLTIP = 'Bulk select all relevant filters. This selection will not override any options you have already pre-selected';

interface IPreset {
  id: string,
  label: string,
  tooltip: string,
  isActive: boolean,
  toggle: () => void,
}

const usePresets = (): IPreset[] => {
  const { getDraft, resetTo, setActiveSectionIds } = useCorporateBondScreenerSidebarFilterContext();
  const { presets } = useBondScreenerColumnsContext();

  const createToggle = useCallback((preset: IBondScreenerPresetConfig) => (): void => {
    const current = getDraft();
    const isCurrentlyActive = isPresetActive(preset, current);

    resetTo(isCurrentlyActive ? removePresetFilters(preset, current) : applyPresetFilters(preset, current));

    if (!isCurrentlyActive) {
      const sectionIds = getPresetSectionIds(preset);
      setActiveSectionIds((prev) => {
        const existing = new Set(prev);
        const toAdd = sectionIds.filter((id) => !existing.has(id));
        return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
      });
    }
  }, [getDraft, resetTo, setActiveSectionIds]);

  const draft = getDraft();

  return presets.map((preset) => ({
    id: preset.id,
    label: preset.label,
    tooltip: PRESET_TOOLTIP,
    isActive: isPresetActive(preset, draft),
    toggle: createToggle(preset),
  }));
};

export default usePresets;
